import React, { useState } from 'react'
import { FiEdit, FiPause, FiPlay, FiTrash2 } from 'react-icons/fi'
import { GoQuestion } from 'react-icons/go'
import useSWR from 'swr'
import Spinner from '../Spinner'
import { Tooltip } from '../../layout/sidebar'
import api, { fetchAll } from '../../utils/api'
import { ErrorNotification, SuccessNotification, truncateString } from '../../utils'
import InviteModal from '../modals/InviteModal'
import EidtInviteModal from '../modals/EidtInviteModal'
import ConfirmActionModal from './ConfirmActionModal'

const getErrorMessage = (error, fallback) =>
  error.response?.data?.detail || error.response?.data || error.message || fallback

const getTeamMemberDisplayName = (user) =>
  user?.firstname ? `${user.firstname} ${user.lastname}` : user?.email

const getTeamMemberActionConfig = (action, user) => {
  const displayName = getTeamMemberDisplayName(user)

  const configs = {
    suspend: {
      title: 'Suspend Team Member',
      message: `Suspend ${user.email}? They will not be able to log in.`,
      confirmLabel: 'Suspend',
      variant: 'warning',
      icon: FiPause,
    },
    reactivate: {
      title: 'Reactivate Team Member',
      message: `Reactivate ${displayName}? They will be able to log in again.`,
      confirmLabel: 'Reactivate',
      variant: 'primary',
      icon: FiPlay,
    },
    delete: {
      title: 'Delete Team Member',
      message: `Permanently remove ${displayName}? Their products and services will be reassigned to you before deletion. This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
      icon: FiTrash2,
    },
  }

  return configs[action]
}

const TeamMembersTab = ({ inviteModal, setInviteModal, editRoleModal, setEditRoleModal, selectedTeam, setSelectedTeam }) => {
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [pendingAction, setPendingAction] = useState(null)

  const { data: managedUsers, error, isLoading, mutate: mutateTeamMembers } = useSWR(
    'managed-users-list',
    () => fetchAll('managed-users/')
  )

  const closeConfirmModal = () => {
    if (actionLoadingId) return
    setPendingAction(null)
  }

  const handleSuspend = async (user) => {
    setActionLoadingId(user.id)
    try {
      await api.patch(`managed-users/${user.id}/suspend/`)
      SuccessNotification('Team member suspended successfully.')
      mutateTeamMembers()
      setPendingAction(null)
    } catch (err) {
      ErrorNotification(getErrorMessage(err, 'Failed to suspend team member.'))
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleReactivate = async (user) => {
    setActionLoadingId(user.id)
    try {
      await api.patch(`managed-users/${user.id}/reactivate/`)
      SuccessNotification('Team member reactivated successfully.')
      mutateTeamMembers()
      setPendingAction(null)
    } catch (err) {
      ErrorNotification(getErrorMessage(err, 'Failed to reactivate team member.'))
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleDelete = async (user) => {
    setActionLoadingId(user.id)
    try {
      await api.delete(`managed-users/${user.id}/`)
      SuccessNotification('Team member deleted successfully.')
      mutateTeamMembers()
      setPendingAction(null)
    } catch (err) {
      ErrorNotification(getErrorMessage(err, 'Failed to delete team member.'))
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleConfirmAction = () => {
    if (!pendingAction) return

    const { type, user } = pendingAction
    if (type === 'suspend') handleSuspend(user)
    if (type === 'reactivate') handleReactivate(user)
    if (type === 'delete') handleDelete(user)
  }

  const confirmConfig = pendingAction
    ? getTeamMemberActionConfig(pendingAction.type, pendingAction.user)
    : null

  return (
    <>
      {editRoleModal && <EidtInviteModal
        onClose={() => setEditRoleModal(false)}
        selectedTeam={selectedTeam}
        onConfirm={() => {
          mutateTeamMembers()
          setEditRoleModal(false)
        }}
      />}

      {inviteModal && <InviteModal
        mutate={() => {
          mutateTeamMembers()
        }}
        onClose={() => setInviteModal(false)}
        onConfirm={() => { }}
      />}

      {confirmConfig && (
        <ConfirmActionModal
          isOpen={Boolean(pendingAction)}
          onClose={closeConfirmModal}
          onConfirm={handleConfirmAction}
          loading={Boolean(actionLoadingId)}
          {...confirmConfig}
        />
      )}

      <div className="space-y-4">
        <div className="border-b pb-3 border-gray-200 dark:border-border-dark w-full flex flex-col md:flex-row justify-between">
          <div>
            <h1 className="text-base font-bold dark:text-white">
              Team Members
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Team managers and users who login as the current service provider or processor and perform the same actions like them.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="h-96 grid items-center justify-center">
            <Spinner />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 dark:text-red-400">
              Failed to load team members. Please try again.
            </div>
          </div>
        ) : (
          <div className="w-full">
            <div className="flex flex-col">
              <div className="-m-1.5 overflow-x-auto">
                <div className="p-1.5 min-w-full inline-block align-middle">
                  <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                      <thead className="bg-transparent dark:bg-secondaryDark">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 w-1/3">User</th>
                          <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 w-2/3">
                            <div className="flex flex-row items-center gap-2">
                              <p>Roles & Status</p>
                              <Tooltip position="left" text="User roles and invitation status">
                                <GoQuestion className="h-4 w-4" />
                              </Tooltip>
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:border-border-dark dark:divide-border-dark">
                        {Array.isArray(managedUsers) && managedUsers.length > 0 ? (
                          managedUsers.map((user) => {
                            const roleCount = user?.roles_count || user?.assigned_roles_count || user?.assigned_roles?.length || 0
                            return (
                              <tr key={user.id} className="text-[13px]">
                                <td className="px-6 hover:bg-gray-50 dark:hover:bg-secondaryDark py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2">
                                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200">
                                    <p className="text-sm uppercase font-bold">
                                      {user?.firstname ? `${user?.firstname?.charAt(0)}${user?.lastname?.charAt(0) || ''}` : user?.email?.charAt(0)?.toUpperCase() || 'N/A'}
                                    </p>
                                  </div>
                                  <div className="flex flex-col">
                                    <p className="text-sm font-medium">
                                      {user?.firstname ? `${user?.firstname} ${user?.lastname}` : 'Pending Setup'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {user?.email}
                                    </p>
                                  </div>
                                </td>

                                <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                                  <div className="flex flex-col">
                                    <p className="capitalize">
                                      {roleCount > 0 ? `${roleCount} Role(s)` : 'No Roles Assigned'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {user?.accessible_modules?.length > 0
                                        ? `Access to: ${truncateString(user?.accessible_modules?.join(', '), 20)}`
                                        : 'No module access'}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Status:{' '}
                                      <span
                                        className={`capitalize ${
                                          user?.is_active
                                            ? 'text-green-600'
                                            : user?.is_active === false
                                              ? 'text-red-600'
                                              : 'text-gray-500'
                                        }`}
                                      >
                                        {user?.is_active
                                          ? 'Active'
                                          : user?.is_active === false
                                            ? 'Suspended'
                                            : 'Pending Setup'}
                                      </span>
                                    </p>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <Tooltip text="Edit Roles">
                                      <button
                                        disabled={actionLoadingId === user.id}
                                        onClick={() => {
                                          setSelectedTeam(user)
                                          setEditRoleModal(true)
                                        }}
                                        className="text-gray-400 hover:text-yellowcolor disabled:opacity-50"
                                      >
                                        <FiEdit size={16} />
                                      </button>
                                    </Tooltip>
                                    {user.is_active === true && (
                                      <Tooltip text="Suspend Team Member">
                                        <button
                                          disabled={actionLoadingId === user.id}
                                          onClick={() => setPendingAction({ type: 'suspend', user })}
                                          className="text-gray-400 hover:text-orange-500 disabled:opacity-50"
                                        >
                                          <FiPause size={16} />
                                        </button>
                                      </Tooltip>
                                    )}
                                    {user.is_active === false && (
                                      <Tooltip text="Reactivate Team Member">
                                        <button
                                          disabled={actionLoadingId === user.id}
                                          onClick={() => setPendingAction({ type: 'reactivate', user })}
                                          className="text-gray-400 hover:text-green-500 disabled:opacity-50"
                                        >
                                          <FiPlay size={16} />
                                        </button>
                                      </Tooltip>
                                    )}
                                    <Tooltip text="Delete Team Member">
                                      <button
                                        disabled={actionLoadingId === user.id}
                                        onClick={() => setPendingAction({ type: 'delete', user })}
                                        className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                                      >
                                        <FiTrash2 size={16} />
                                      </button>
                                    </Tooltip>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        ) : (
                          <tr>
                            <td colSpan={3} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                              No team members found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default TeamMembersTab
